import { FileUploadImportConfig } from '@oneschema/react';
import { Button, colors, Icon, ModalActions, ModalHeader, Spinner, Stack, TitleWithHelp } from '@ui-components/neo.tax';
import { ReactElement, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { HELP_CONNECT_PAYROLL_CSV_URL } from '@neo.tax/shared/constants';
import { PayrollProvider as GraphQLPayrollProvider, PayrollProvider } from '@neo.tax/shared/graphql';
import { useCurrentCompany } from '@neo.tax/shared/hooks';
import { ToastConfigState, useAnalyticId } from '@neo.tax/shared/state';

import {
  OneSchemaWrapper,
  RIPPLING_PAYROLL_UPLOAD_TEMPLATE_KEY,
  RD_PAYROLL_UPLOAD_TEMPLATE_KEY,
} from '../../../../one-schema-wrapper';
import { getProviderInfo } from '../../utils';
import { PayrollExpenseCsvUploadInstructions, StyledCsvConnectProvider } from '../connect-csv-provider';
import { ConnectProviderSlideProps, ProviderAnalyticLiteralIds } from '../types';
import { useRdPayrollCostUpload } from './useRdPayrollCostUpload';

export const ConnectRdPayrollCostProvider = ({
  close,
  fiscalYearId,
  restart,
  ...otherProps
}: ConnectProviderSlideProps['provider'] & {
  fiscalYearId: string;
  close: () => void;
  restart: () => void;
}): ReactElement => {
  const { company } = useCurrentCompany();
  const { DownloadCsvInstructionsComponent, analyticIds, fullName, logo, name } = getProviderInfo(otherProps);
  const [shouldOpenOneSchema, setShouldOpenOneSchema] = useState<boolean>(false);
  const [isLaunchingOneSchema, setIsLaunchingOneSchema] = useState<boolean>(false);

  // Use different OneSchema template key for Rippling vs other providers
  const isRippling = otherProps.providerType === 'payroll' && otherProps.providerName === PayrollProvider.Rippling;
  const templateKey = isRippling ? RIPPLING_PAYROLL_UPLOAD_TEMPLATE_KEY : RD_PAYROLL_UPLOAD_TEMPLATE_KEY;

  // Determine which payroll provider to use based on the provider type
  const payrollProvider: GraphQLPayrollProvider =
    otherProps.providerType === 'payroll' ? otherProps.providerName : GraphQLPayrollProvider.SelfReported;

  const { handleSuccess, oneSchemaJwt, permanentUrl, signedUrl } = useRdPayrollCostUpload({
    companyId: company?.id ?? '',
    fiscalYearId,
    isOpen: shouldOpenOneSchema,
    payrollProvider,
  });

  if (!analyticIds.previousStepId) {
    throw new Error(`Please provide a 'previous step' id for ${name}`);
  }
  if (!analyticIds.uploadInputId) {
    throw new Error(`Please provide an 'upload input' id for ${name}`);
  }
  if (!analyticIds.closeAfterSuccessButtonId) {
    throw new Error(`Please provide an 'close after success button' id for ${name}`);
  }

  const literalAnalyticIds: ProviderAnalyticLiteralIds = {
    connectId: useAnalyticId(analyticIds.connectId),
    cancelConnectionId: useAnalyticId(analyticIds.cancelConnectionId),
    previousStepId: useAnalyticId(analyticIds.previousStepId),
    uploadInputId: useAnalyticId(analyticIds.uploadInputId),
    closeAfterSuccessButtonId: useAnalyticId(analyticIds.closeAfterSuccessButtonId),
  };

  const resetState = useCallback((): void => {
    close();
    setShouldOpenOneSchema(false);
    setIsLaunchingOneSchema(false);
  }, [close]);

  const handleContinue = (): void => {
    setShouldOpenOneSchema(true);
    setIsLaunchingOneSchema(true);
  };

  const handleCancel = (): void => {
    resetState();
  };

  const handleGoBack = (): void => {
    restart();
  };

  const onError = (error: unknown): void => {
    const errorMessage =
      (error instanceof Object || error instanceof Error) && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'An unexpected error occurred.';
    ToastConfigState.pushErrorToast('Error uploading R&D payroll cost file', {
      description: `OneSchema error: ${errorMessage}`,
      duration: 'MEDIUM',
    });
    console.error('OneSchema error:', { companyId: company?.id, oneSchemaJwt, permanentUrl, signedUrl, error });
    resetState();
  };

  const onSuccess = async (): Promise<void> => {
    await handleSuccess();
    resetState();
  };

  if (!company) {
    return <></>;
  }

  const isOneSchemaOpen = shouldOpenOneSchema && !!oneSchemaJwt && !!signedUrl && !!permanentUrl;

  const importConfig: FileUploadImportConfig = {
    format: 'csv',
    type: 'file-upload',
    url: signedUrl ?? '',
  };

  return (
    <>
      <StyledCsvConnectProvider>
        <ModalHeader
          icon={<Icon node={logo} size={12} variant="none" />}
          iconBgColor={colors.white}
          title={<TitleWithHelp title={`${fullName} Instructions`} href={HELP_CONNECT_PAYROLL_CSV_URL} />}
        />
        {shouldOpenOneSchema ? (
          <Stack direction="column" paddingY={4} alignItems="center" justifyContent="center" minHeight={200}>
            <Spinner size="xl" />
          </Stack>
        ) : (
          <>
            <Stack direction="column" paddingY={4}>
              {DownloadCsvInstructionsComponent ? (
                <DownloadCsvInstructionsComponent />
              ) : (
                <PayrollExpenseCsvUploadInstructions />
              )}
            </Stack>
            <ModalActions>
              <Button
                variant="white"
                rounded="regular"
                size="base"
                onClick={handleGoBack}
                id={literalAnalyticIds.previousStepId}
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                rounded="regular"
                size="base"
                onClick={handleContinue}
                isLoading={isLaunchingOneSchema}
              >
                Continue
              </Button>
            </ModalActions>
          </>
        )}
      </StyledCsvConnectProvider>

      {createPortal(
        <OneSchemaWrapper
          isOpen={isOneSchemaOpen}
          userJwt={oneSchemaJwt ?? ''}
          templateKey={templateKey}
          importConfig={importConfig}
          onCancel={handleCancel}
          onError={onError}
          onSuccess={onSuccess}
        />,
        document.body,
      )}
    </>
  );
};
